import { useQueryClient, type InfiniteData } from '@tanstack/vue-query'
import { useToast } from 'vue-toastification'
import type { Notification } from '@/types/global'
import {
  normalizeRealtimeBusMessage,
  useRealtimeBus,
  type RealtimeBusNotification
} from '@/composables/useRealtimeBus'

const notificationsPageSize = 10

type NotificationsPage = {
  notifications: Notification[]
  nextPage?: number
}

type NotificationBusMessage = {
  event?: string
  notification?: Notification
}

/**
 * Owns the app-wide private notification inbox connection. It remains active
 * across route changes, independently of page-level presence and call buses.
 */
export function useNotificationsRealtime() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const receivedNotificationIDs = new Set<number>()

  function handleNotification(event: RealtimeBusNotification<NotificationBusMessage>) {
    const message = normalizeRealtimeBusMessage<NotificationBusMessage>(event.message ?? event)
    const notification = message.notification
    if (message.event !== 'notification' || !notification || typeof notification.id !== 'number') return
    if (receivedNotificationIDs.has(notification.id)) return

    receivedNotificationIDs.add(notification.id)
    queryClient.setQueryData<InfiniteData<NotificationsPage, number>>(['notifications'], (current) => {
      if (!current) return current
      if (current.pages.some((page) => page.notifications.some((item) => item.id === notification.id))) {
        return current
      }

      const firstPage = current.pages[0]
      if (!firstPage) return current

      return {
        ...current,
        pages: [
          {
            ...firstPage,
            notifications: [notification, ...firstPage.notifications].slice(0, notificationsPageSize)
          },
          ...current.pages.slice(1)
        ]
      }
    })

    // The durable inbox remains authoritative if this event raced the initial
    // request or was replayed after reconnecting.
    void queryClient.invalidateQueries({ queryKey: ['notifications'] })

    // Replayed events repair the inbox but must not replay a history of toast
    // notifications after a reconnect.
    const age = Date.now() - new Date(notification.date).getTime()
    if (Number.isFinite(age) && age >= 0 && age < 60_000) {
      toast.info(`${notification.subject}: ${notification.content}`)
    }
  }

  return useRealtimeBus<NotificationBusMessage>({
    channels: () => ['notification'],
    onNotification: handleNotification
  })
}
